/**
 * /overview as one CSV (R19): a section per box in layout order — the box
 * tables the chat reads, and /looker's own app sections — with provenance
 * lines naming the scope, window, currency, objective, Result definition,
 * cut, sync, channels, backfill and targets date. In a facts box a value that
 * holds nothing (÷0, no previous period, no target) is "—" and a Result the
 * platform did not report "ไม่ได้รายงาน", as the page shows them, never 0
 * (rules-04 D4); a label, a dimension or a marker with nothing in it stays
 * empty. The app boxes are /looker's sections, as /looker writes them.
 *
 * @module services/overview/overviewCsv
 */
import type { TFunction } from 'i18next';
import { downloadCsv, type CsvCell } from '@/services/csv';
import { isHiddenWhenEmpty, type AppTileState } from '@/hooks/useLookerAppTiles';
import type { BackfillState } from '@/hooks/useLookerBackfill';
import type { ChannelFreshnessState } from '@/hooks/useLookerChannelFreshness';
import type { CoverageState } from '@/hooks/useLookerCoverage';
import { appCsvSection, backfillLine, coverageSection, syncLine, type DashboardCsvLabels } from '@/services/looker/lookerCsv';
import type { AppWidget } from '@/services/looker/lookerAppTiles';
import { platformLabel } from '@/utils/platformLabel';
import { currencyLine, fileRows } from './boxTableParts';
import { APP_BOX, OVERVIEW_LAYOUT, type BoxTable, type OverviewPageInput } from './overviewTables';
import type { OverviewView } from './overviewView';

const tableSection = (b: BoxTable, l: DashboardCsvLabels): CsvCell[][] => {
  if (b.status === 'failed') return [[b.title], [l.appFailed]];
  // An empty box keeps its words: under either card every facts box says only what the page's card says (rules-04 A1,
  // H review F2, I2).
  if (b.status === 'empty') return [[b.title], ...(b.notesOnly ? [] : [[l.tileEmpty]]), ...b.notes.map((n) => [n])];
  if (b.status === 'needsWorkspace') return [[b.title], [l.needsWorkspace]];
  if (b.status === 'loading') return [[b.title], [l.loading]];
  return [
    [b.title], b.columns, ...fileRows(b), ...b.notes.map((n) => [n]),
    ...(b.resultThrough ? [[l.resultThrough(b.resultThrough)]] : []),
  ];
};

export const overviewCsvRows = (input: OverviewPageInput, labels: DashboardCsvLabels): CsvCell[][] =>
  OVERVIEW_LAYOUT.flatMap((id): CsvCell[][] => {
    const widget = APP_BOX[id];
    if (widget) {
      const state = input.app[widget];
      if (isHiddenWhenEmpty(state)) return [];
      const box8 = widget === 'dataQuality' && state.status === 'ready' ? coverageSection(input.coverage) : [];
      return [...appCsvSection({ title: input.appTitles[widget] }, state, labels), ...box8, []];
    }
    const b = input.tables.find((x) => x.id === id);
    return b ? [...tableSection(b, labels), []] : [];
  });

const mappingLine = (input: OverviewPageInput, l: DashboardCsvLabels, t: TFunction): string | null => {
  const m = input.mapping;
  if (!m) return null;
  if (m.failed) return l.resultMappingFailed;
  if (m.awareness === 'reach') return l.resultMappingReach;
  return m.central ? t('looker.resultMapping.central') : null;
};

export const overviewProvenance = (input: OverviewPageInput, l: DashboardCsvLabels, t: TFunction): string[] => {
  const w = input.windows;
  const targeted = input.tables.some((b) => b.targets);
  return [
    input.title,
    input.allWorkspaces ? l.allWorkspaces : input.workspaceName,
    l.period(w.current.start, w.current.end, w.previous.start, w.previous.end),
    input.latestDataDate ? l.dataAsOf(input.latestDataDate) : null,
    currencyLine(input.money, l),
    t('overview.csv.objectiveLine', { objective: input.objectiveLabel }),
    mappingLine(input, l, t),
    w.cutApplied ? l.resultThrough(w.through) : null,
    syncLine(input.freshness, l),
    input.channels.length > 0 ? l.channels(input.channels.map((c) => platformLabel(c)).join(', ')) : l.allChannels,
    backfillLine(input.backfill, l),
    targeted ? l.targetsAsOf(w.through) : null,
  ].filter((line): line is string => Boolean(line));
};

export const downloadOverviewCsv = (input: OverviewPageInput, labels: DashboardCsvLabels, t: TFunction): void => {
  const w = input.windows.current;
  downloadCsv(`executive-overview_${w.start}_${w.end}.csv`, overviewCsvRows(input, labels), {
    provenance: overviewProvenance(input, labels, t),
  });
};

/** A file or a chat written while a box loads would carry "loading" or miss a line. */
export const pageSettled = (
  view: OverviewView,
  app: Readonly<Record<AppWidget, AppTileState>>,
  reads: { backfill: BackfillState; freshness: ChannelFreshnessState; coverage: CoverageState },
): boolean => {
  const boxes = [view.scorecards, view.result, view.trend, view.share, view.objectives, view.channels, view.accounts, view.campaigns];
  return boxes.every((s) => s === null || s.status !== 'loading')
    && Object.values(app).every((s) => s.status !== 'loading')
    && [reads.backfill, reads.freshness, reads.coverage].every((s) => s.status !== 'loading');
};
